import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import Button from '../../components/ui/button';
import Header from '../../components/navbar/Header';
import Footer from '../../components/footer/footer';
import ClientPaymentModel from '../../model/client/client-payment-model';

/**
 * Page de confirmation de réservation
 * Affichée après un paiement Stripe réussi OU après une réservation directe (sans paiement)
 */
const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'fr';

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<any>(null);
  const [isDirectBooking, setIsDirectBooking] = useState(false);

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const businessName = searchParams.get('business_name');
  const stripeAccountId = searchParams.get('stripe_account');

  useEffect(() => {
    const verifyBooking = async () => {
      // Réservation directe sans paiement (Plan Junior)
      if (bookingId && !sessionId) {
        setIsDirectBooking(true);
        setSessionStatus({
          company: { businessName: businessName || '' }
        });
        setIsLoading(false);
        return;
      }

      // Réservation avec paiement Stripe
      if (!sessionId) {
        setError('Informations de session manquantes');
        setIsLoading(false);
        return;
      }

      try {
        // Vérifier le statut de la session Stripe
        const response = await ClientPaymentModel.getInstance().checkSessionStatus(
          sessionId,
          stripeAccountId || undefined
        );

        if (response.success && response.data) {
          setSessionStatus(response.data);

          // Si le paiement n'est pas encore complété, c'est peut-être en cours de traitement
          if (response.data.paymentStatus !== 'paid') {
            setError('Le paiement est en cours de traitement. Veuillez rafraîchir la page dans quelques instants.');
          }
        } else {
          setError('Impossible de vérifier le statut du paiement');
        }
      } catch (err: any) {
        console.error('Error verifying payment:', err);
        setError(err.message || 'Erreur lors de la vérification du paiement');
      } finally {
        setIsLoading(false);
      }
    };

    verifyBooking();
  }, [sessionId, bookingId, businessName]);

  const handleBookAnother = () => {
    if (isDirectBooking) {
      navigate(-1);
    } else if (sessionStatus?.company) {
      const domain = sessionStatus.company.domain || 'services';
      const businessName = sessionStatus.company.businessName;
      navigate(`/${currentLang}/${domain}/${businessName}`);
    } else {
      navigate(`/${currentLang}`);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 pt-20 pb-16">
        <div className="max-w-2xl mx-auto px-4 py-8">
          {isLoading ? (
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t('payment.verifying', 'Vérification du paiement...')}
              </h2>
              <p className="text-gray-600">
                {t('payment.pleaseWait', 'Veuillez patienter')}
              </p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t('payment.error', 'Erreur')}
              </h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button onClick={handleBookAnother} className="mx-auto">
                {t('payment.backToHome', 'Retour à l\'accueil')}
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              {/* En-tête succès */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {isDirectBooking
                    ? t('booking.successTitle', 'Réservation confirmée !')
                    : t('payment.successTitle', 'Paiement réussi !')
                  }
                </h1>
                <p className="text-lg text-gray-600">
                  {isDirectBooking
                    ? t('booking.successMessage', 'Votre réservation a été confirmée avec succès')
                    : t('payment.successMessage', 'Votre paiement a été traité avec succès')
                  }
                </p>
              </div>

              {/* Détails de la session (uniquement pour les paiements) */}
              {sessionStatus && !isDirectBooking && (
                <div className="bg-gray-50 rounded-xl p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('payment.details', 'Détails du paiement')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        {t('payment.amount', 'Montant')}
                      </span>
                      <span className="font-semibold text-gray-900">
                        {Number(sessionStatus.amountTotal).toFixed(2)} €
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        {t('payment.status', 'Statut')}
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        {t('payment.paid', 'Payé')}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Message de confirmation */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800">
                  {t('payment.confirmationEmail', 'Un e-mail de confirmation a été envoyé à votre adresse e-mail avec tous les détails de votre réservation.')}
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-center">
                <Button
                  onClick={handleBookAnother}
                  className="bg-primary hover:bg-primary-dark px-8"
                >
                  {t('payment.bookAnother', 'Prendre un autre rendez-vous')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PaymentSuccess;
